import { useEffect, useRef, useState, type PointerEvent } from 'react';
import { Headset } from 'lucide-react';
import {
  arrastePassouDoClique,
  gravarPosicaoMia,
  lerPosicaoMia,
  limitarPosicao,
  type PosicaoMia,
} from './miaPosicao';
import styles from './AssistenteWidget.module.css';

type BotaoMiaProps = {
  onAbrir: () => void;
};

export function BotaoMia({ onAbrir }: BotaoMiaProps) {
  const caixaRef = useRef<HTMLDivElement>(null);
  const [posicao, setPosicao] = useState<PosicaoMia | null>(() => lerPosicaoMia());
  const [arraste, setArraste] = useState<PosicaoMia | null>(null);
  const arrastouRef = useRef(false);
  const capturadoRef = useRef(false);
  const lugarRef = useRef<PosicaoMia | null>(null);

  useEffect(() => {
    const ajustar = () => {
      const caixa = caixaRef.current;
      if (!caixa) return;
      setPosicao((atual) => {
        if (!atual) return atual;
        const limitada = limitarPosicao(
          atual.left,
          atual.top,
          window.innerWidth,
          window.innerHeight,
          caixa.offsetWidth,
          caixa.offsetHeight,
        );
        if (limitada.left === atual.left && limitada.top === atual.top) return atual;
        gravarPosicaoMia(limitada);
        return limitada;
      });
    };
    window.addEventListener('resize', ajustar);
    return () => window.removeEventListener('resize', ajustar);
  }, []);

  function onPointerDown(event: PointerEvent<HTMLButtonElement>) {
    if (event.button > 0) return;
    event.stopPropagation();
    const caixa = caixaRef.current?.getBoundingClientRect();
    if (!caixa) return;
    const origem = {
      x: event.clientX,
      y: event.clientY,
      left: caixa.left,
      top: caixa.top,
      w: caixa.width,
      h: caixa.height,
    };
    arrastouRef.current = false;
    capturadoRef.current = true;

    const mover = (ev: globalThis.PointerEvent) => {
      if (!capturadoRef.current) return;
      const dx = ev.clientX - origem.x;
      const dy = ev.clientY - origem.y;
      if (!arrastePassouDoClique(dx, dy) && !arrastouRef.current) return;
      arrastouRef.current = true;
      const proxima = limitarPosicao(
        origem.left + dx,
        origem.top + dy,
        window.innerWidth,
        window.innerHeight,
        origem.w,
        origem.h,
      );
      lugarRef.current = proxima;
      setArraste(proxima);
    };
    const soltar = () => {
      document.removeEventListener('pointermove', mover);
      document.removeEventListener('pointerup', soltar);
      if (!capturadoRef.current) return;
      capturadoRef.current = false;
      if (!arrastouRef.current) {
        setArraste(null);
        return;
      }
      const solta = lugarRef.current;
      if (solta) {
        setPosicao(solta);
        gravarPosicaoMia(solta);
      }
      setArraste(null);
    };
    document.addEventListener('pointermove', mover);
    document.addEventListener('pointerup', soltar);
  }

  const lugar = arraste ?? posicao;

  return (
    <div
      ref={caixaRef}
      className={`${styles.launcher} ${styles.ajuda} ${lugar ? styles.launcherSolto : styles.posicaoPadrao} ${arraste ? styles.launcherArrastando : ''}`}
      style={lugar ? { left: lugar.left, top: lugar.top } : undefined}
    >
      <button
        type="button"
        className={styles.alca}
        aria-label="Mover Mia"
        onPointerDown={onPointerDown}
        onClick={(event) => event.preventDefault()}
      >
        <Headset size={18} />
      </button>
      <button type="button" className={styles.rotulo} onClick={onAbrir} aria-label="Mia Ajuda">
        {/* Celular (ate 768px): so o icone, num botao redondo; ver AssistenteWidget.module.css. */}
        <Headset size={22} className={styles.rotuloIcone} aria-hidden />
        <span className={styles.rotuloTexto}>Mia Ajuda</span>
      </button>
    </div>
  );
}
